// Header — with auth menu + admin link + language toggle
import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User, Shield, ListOrdered, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { asset } from "@/lib/siteAssets";
import { LanguageToggle } from "@/components/LanguageToggle";
import { NotificationPanel } from "@/components/NotificationPanel";
import { track, trackNavClick } from "@/lib/analytics";

export const Header = ({ cartCount, onCartOpen, onAuthOpen }) => {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLang();
  const { user, profile, isAdmin, signOut } = useAuth();
  const { pathname } = useLocation();
  const isCheckout = pathname === "/checkout";

  // eventLabel is always English regardless of site language, so the same
  // link always produces the same Mixpanel event name — see trackNavClick.
  const links = [
    { to: "/", label: t.nav.home, eventLabel: "Home" },
    { to: "/about", label: t.nav.about, eventLabel: "About" },
    { to: "/products", label: t.nav.products, eventLabel: "Products" },
    { to: "/contact", label: t.nav.contact, eventLabel: "Contact" },
    { to: "/offers", label: t.nav.offers, eventLabel: "Offers" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-md bg-[#F5E6CC]/85 border-b border-[#5A2D1D]/20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
            <img
              src={asset("brand/logo-v2.webp")}
              alt="Annapurna Home Pickles"
              className="w-9 h-9 sm:w-12 sm:h-12 rounded-full object-cover shadow-sm"
            />
            <div className="leading-tight">
              <div className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-[#5A2D1D]">
                Annapurna
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#556B2F] font-semibold">
                Home Pickles
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to + l.label}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => trackNavClick(l.eventLabel, "header_nav")}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors relative group ${
                    isActive
                      ? "text-[#5A2D1D] font-bold"
                      : "text-[#2E1A12]/80 hover:text-[#5A2D1D]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span className={`absolute -bottom-1 left-0 h-[2px] bg-[#556B2F] transition-all ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageToggle className="hidden sm:inline-flex" />
            <NotificationPanel />

            {!isCheckout && (
              user ? (
                <div className="relative">
                  <button
                    data-testid="user-menu-button"
                    onClick={() => setMenuOpen((v) => !v)}
                    className="inline-flex items-center gap-2 border border-[#5A2D1D]/30 px-2 sm:px-4 py-2 rounded-full text-sm font-semibold text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline max-w-[100px] truncate">
                      {profile?.name || user.email?.split("@")[0]}
                    </span>
                  </button>
                  {menuOpen && (
                    <div
                      data-testid="user-menu-dropdown"
                      className="absolute right-0 top-full mt-2 w-56 bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm shadow-lg z-50 py-2"
                      onMouseLeave={() => setMenuOpen(false)}
                    >
                      <Link
                        to="/profile"
                        onClick={() => { setMenuOpen(false); trackNavClick("My Profile", "header_user_menu"); }}
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[#5A2D1D]/10 text-[#2E1A12]"
                      >
                        <User className="w-4 h-4" />
                        My Profile
                      </Link>
                      <Link
                        to="/my-orders"
                        data-testid="menu-my-orders"
                        onClick={() => { setMenuOpen(false); trackNavClick("My Orders", "header_user_menu"); }}
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[#5A2D1D]/10 text-[#2E1A12]"
                      >
                        <ListOrdered className="w-4 h-4" />
                        {t.nav.my_orders}
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          data-testid="menu-admin"
                          onClick={() => { setMenuOpen(false); trackNavClick("Admin Dashboard", "header_user_menu"); }}
                          className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-[#5A2D1D]/10 text-[#2E1A12]"
                        >
                          <Shield className="w-4 h-4" />
                          {t.nav.admin}
                        </Link>
                      )}
                      <button
                        data-testid="menu-sign-out"
                        onClick={() => {
                          setMenuOpen(false);
                          track("sign_out_clicked");
                          signOut();
                        }}
                        className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm hover:bg-[#5A2D1D]/10 text-[#2E1A12]"
                      >
                        <LogOut className="w-4 h-4" />
                        {t.nav.logout}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  data-testid="header-signin-button"
                  onClick={() => { onAuthOpen(); trackNavClick("Sign In", "header"); }}
                  className="hidden sm:inline-flex items-center gap-2 border border-[#5A2D1D]/30 px-4 py-2 rounded-full text-sm font-semibold text-[#5A2D1D] hover:bg-[#5A2D1D]/5"
                >
                  <User className="w-4 h-4" />
                  {t.nav.login}
                </button>
              )
            )}

            <button
              data-testid="cart-open-button"
              onClick={() => { onCartOpen(); track("cart_opened", { item_count: cartCount, source: "header" }); }}
              className="relative inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-4 py-2.5 rounded-full font-semibold text-sm transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden md:inline">Cart</span>
              {cartCount > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#556B2F] text-[#F5E6CC] text-[11px] font-bold"
                >
                  {cartCount}
                </span>
              )}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-[#5A2D1D]"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden pb-4 border-t border-[#5A2D1D]/15 pt-3">
            <div className="flex flex-col gap-3">
              {links.map((l) => (
                <NavLink
                  key={l.to + l.label}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => { setOpen(false); trackNavClick(l.eventLabel, "header_nav_mobile"); }}
                  className={({ isActive }) =>
                    `text-base font-medium py-1 ${isActive ? "text-[#5A2D1D] font-bold" : "text-[#2E1A12]"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <LanguageToggle />
              {user && isCheckout && (
                <>
                  <div className="border-t border-[#5A2D1D]/15 pt-3 mt-1 flex flex-col gap-3">
                    <Link
                      to="/profile"
                      onClick={() => { setOpen(false); trackNavClick("My Profile", "mobile_menu_checkout"); }}
                      className="flex items-center gap-2 text-base font-medium text-[#2E1A12]"
                    >
                      <User className="w-4 h-4 text-[#5A2D1D]" /> My Profile
                    </Link>
                    <Link
                      to="/my-orders"
                      onClick={() => { setOpen(false); trackNavClick("My Orders", "mobile_menu_checkout"); }}
                      className="flex items-center gap-2 text-base font-medium text-[#2E1A12]"
                    >
                      <ListOrdered className="w-4 h-4 text-[#5A2D1D]" /> {t.nav.my_orders}
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => { setOpen(false); trackNavClick("Admin Dashboard", "mobile_menu_checkout"); }}
                        className="flex items-center gap-2 text-base font-medium text-[#2E1A12]"
                      >
                        <Shield className="w-4 h-4 text-[#5A2D1D]" /> {t.nav.admin}
                      </Link>
                    )}
                    <button
                      onClick={() => { setOpen(false); track("sign_out_clicked"); signOut(); }}
                      className="flex items-center gap-2 text-base font-medium text-[#2E1A12] text-left"
                    >
                      <LogOut className="w-4 h-4 text-[#5A2D1D]" /> {t.nav.logout}
                    </button>
                  </div>
                </>
              )}
              {!user && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onAuthOpen();
                    trackNavClick("Sign In", "mobile_menu");
                  }}
                  className="mt-2 self-start bg-[#5A2D1D] text-[#F5E6CC] px-4 py-2 rounded-full text-sm font-semibold"
                >
                  {t.nav.login}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
